import { jsx, jsxs } from 'react/jsx-runtime';
import { useContext, createContext, useMemo } from 'react';
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/react';
import { Slot } from '@radix-ui/react-slot';
import * as Portal from '@radix-ui/react-portal';
import { AnimatePresence } from 'framer-motion';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import { useBoolState } from '@fabric-app/hooks/useBooleanState';
import { useOnClickOutside } from '@fabric-app/hooks/useOnClickOutside';
import { styled } from 'styled-components';
import { useResponsive } from '../hooks/useResponsive.js';

const FloatAnchorContext = /* @__PURE__ */ createContext(null);
const FloatAnchorRoot = ({ open: openProp, setOpen: setOpenProp, children, offset: offsetOptions = 5, middleware, useTransform = true, placement = "bottom-start" }) => {
  const defaultOpenState = useBoolState();
  const open = useMemo(() => openProp ?? defaultOpenState.value, [
    openProp,
    defaultOpenState.value
  ]);
  const setOpen = useMemo(() => setOpenProp ?? defaultOpenState.set, [
    setOpenProp,
    defaultOpenState.set
  ]);
  const { refs, floatingStyles } = useFloating({
    placement,
    middleware: [
      offset(offsetOptions),
      flip(),
      shift(),
      ...middleware ?? []
    ],
    whileElementsMounted: autoUpdate,
    transform: useTransform
  });
  return /* @__PURE__ */ jsx(FloatAnchorContext.Provider, {
    value: {
      open,
      setOpen,
      refs,
      floatingStyles
    },
    children
  });
};
const FloatAnchorTrigger = ({ children, action = "hover", asChild, preventDefault = false }) => {
  const context = useContext(FloatAnchorContext);
  if (!context) throw new Error("FloatAnchorTrigger must be used within FloatAnchorRoot");
  const { setOpen, open } = context;
  const handlers = useMemo(() => ({
    onMouseEnter: () => action === "hover" && setOpen(true),
    onMouseLeave: () => action === "hover" && setOpen(false),
    onFocus: () => action === "focus" && setOpen(true),
    onBlur: () => action === "focus" && setOpen(false),
    onClick: (e) => {
      if (action !== "click") return;
      if (preventDefault) e.preventDefault();
      setOpen(!open);
    }
  }), [
    action,
    setOpen,
    open,
    preventDefault
  ]);
  const Component = asChild ? Slot : "div";
  useOnClickOutside(context.refs.floating, () => {
    if (!context.open) return;
    context.setOpen(false);
  }, {
    exceptions: typeof window !== "undefined" && context.refs.reference.current instanceof HTMLElement ? [
      context.refs.reference.current
    ] : [],
    disabled: action !== "click"
  });
  return /* @__PURE__ */ jsx(Component, {
    ref: context.refs.setReference,
    ...handlers,
    children
  });
};
const Overlay = styled.div.withConfig({
  displayName: "FloatAnchor__Overlay",
  componentId: "sc-f0654079-0"
})([
  `pointer-events:auto;position:fixed;top:0;left:0;width:100%;height:100%;z-index:125;`
]);
const FloatAnchorFloating = ({ children, asChild, overlay = false, mobileNonAnchored = false, portalLocation = getDefaultRadixPortalContainer() }) => {
  const { isMobileView } = useResponsive();
  const context = useContext(FloatAnchorContext);
  if (!context) throw new Error("FloatAnchorFloating must be used within FloatAnchorRoot");
  const Component = asChild ? Slot : "div";
  return /* @__PURE__ */ jsx(Portal.Root, {
    children: /* @__PURE__ */ jsx(AnimatePresence, {
      children: context.open && /* @__PURE__ */ jsxs(Portal.Portal, {
        container: portalLocation,
        children: [
          overlay && /* @__PURE__ */ jsx(Overlay, {}, "overlay"),
          mobileNonAnchored && isMobileView ? /* @__PURE__ */ jsx(Component, {
            "data-state": context.open ? "open" : "closed",
            ref: context.refs.setFloating,
            children
          }, "floating") : /* @__PURE__ */ jsx(Component, {
            ref: context.refs.setFloating,
            style: context.floatingStyles,
            "data-state": context.open ? "open" : "closed",
            children
          }, "floating")
        ]
      }, "portal")
    })
  });
};
const FloatAnchor = {
  Root: FloatAnchorRoot,
  Trigger: FloatAnchorTrigger,
  Floating: FloatAnchorFloating
};

export { FloatAnchor };
//# sourceMappingURL=FloatAnchor.js.map
